import { useMemo, useRef, useEffect, useState } from "react";
import type { ReactQuillProps } from "react-quill";
import ReactQuill, { Quill } from "react-quill";
import styled from "@emotion/styled";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";

// iframe src에 허용된 도메인 목록 (영상 embed 용도)
const ALLOWED_IFRAME_DOMAINS = [
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
  "vimeo.com",
  "player.vimeo.com",
  "dailymotion.com",
  "www.dailymotion.com",
] as const;

// iframe src가 허용된 도메인인지 검증
const isAllowedIframeSrc = (src: string): boolean => {
  try {
    const url = new URL(src);
    return ALLOWED_IFRAME_DOMAINS.some(
      (domain) => url.hostname === domain || url.hostname.endsWith(`.${domain}`)
    );
  } catch {
    // 유효하지 않은 URL
    return false;
  }
};

// DOMPurify 설정: iframe src 도메인 검증 hook 추가
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "IFRAME") {
    const src = node.getAttribute("src") || "";
    if (!isAllowedIframeSrc(src)) {
      // 허용되지 않은 도메인의 iframe은 제거
      node.parentNode?.removeChild(node);
    }
  }
});

// Quill 설정을 한 번만 실행
let quillInitialized = false;

const initializeQuill = () => {
  if (quillInitialized) return;

  try {
    const Size = Quill.import("attributors/style/size") as any;
    if (Size) {
      Size.whitelist = ["12px", "14px", "16px", "18px", "20px", "22px", "24px"];
      Quill.register(Size, true);
    }

    const Image = Quill.import("formats/image") as any;
    if (Image) {
      Image.sanitize = (url: string) => {
        return ["http", "https", "data", "blob"].some((source) =>
          url.includes(source)
        )
          ? url
          : "//:0";
      };
    }

    quillInitialized = true;
  } catch (error) {
    console.warn("Quill initialization failed:", error);
  }
};

// 초기화 실행
initializeQuill();

interface EditorProps extends Omit<ReactQuillProps, "modules" | "formats"> {
  readOnly?: boolean;
  quillTheme?: string;
}

const QuillWrapper: React.FC<EditorProps> = ({
  readOnly = false,
  quillTheme = "snow",
  value = "",
  onChange,
  ...props
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // value가 변경될 때 강제로 업데이트 (편집 모드에서만)
  useEffect(() => {
    if (!readOnly && mounted && quillRef.current && value) {
      const quill = quillRef.current.getEditor();
      const currentContent = quill.root.innerHTML;
      if (currentContent !== value && currentContent === "<p><br></p>") {
        quill.root.innerHTML = value;
      }
    }
  }, [value, mounted, readOnly]);

  const imageHandler = useMemo(
    () => () => {
      if (readOnly) return;

      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.click();

      input.addEventListener("change", () => {
        const file = input.files?.[0];
        if (file && quillRef.current) {
          const quill = quillRef.current.getEditor();
          const imageUrl = URL.createObjectURL(file);
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", imageUrl, "user");
          quill.setSelection(range.index + 1, 0, "user");
        }
      });
    },
    [readOnly]
  );

  const modules = useMemo(
    () => ({
      toolbar: readOnly
        ? false
        : {
            container: [
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              ["bold", "italic", "underline", "strike"],
              [
                {
                  size: [
                    "12px",
                    "14px",
                    "16px",
                    "18px",
                    "20px",
                    "22px",
                    "24px",
                  ],
                },
              ],
              [{ color: [] }, { background: [] }],
              [{ list: "ordered" }],
              [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
              ["link", "image"],
            ],
            handlers: {
              image: imageHandler,
            },
          },
      clipboard: {
        matchVisual: false,
      },
    }),
    [imageHandler, readOnly]
  );

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "size",
    "color",
    "background",
    "list",
    "indent",
    "align",
    "link",
    "image",
  ];

  // readOnly 모드에서는 Quill 대신 일반 HTML로 렌더링 (모든 HTML 태그 지원)
  // DOMPurify로 XSS 공격 방지
  if (readOnly) {
    return (
      <ReadOnlyContent
        className="ql-editor"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(value || "", {
            ADD_TAGS: ["iframe"],
            ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"],
          }),
        }}
      />
    );
  }

  if (!mounted) {
    return (
      <LoadingContainer readOnly={readOnly}>
        <LoadingText>에디터 로딩 중...</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <ReactQuill
      ref={quillRef}
      value={value || ""}
      theme={quillTheme}
      modules={modules}
      formats={formats}
      readOnly={readOnly}
      onChange={onChange}
      {...props}
    />
  );
};

const ReadOnlyContent = styled.div`
  width: 100%;
  font-family: "Pretendard";
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  word-break: break-word;

  /* Quill 에디터 스타일 호환 */
  & img {
    max-width: 100%;
    height: auto;
  }

  & ul,
  & ol {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }

  & li {
    margin: 0.25em 0;
  }

  & p {
    margin: 0.5em 0;
  }

  & a {
    color: #0066cc;
    text-decoration: underline;
  }

  & table {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
  }

  & th,
  & td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }

  & blockquote {
    border-left: 4px solid #ccc;
    margin: 1em 0;
    padding-left: 1em;
    color: #666;
  }

  & pre,
  & code {
    background-color: #f5f5f5;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: monospace;
  }

  & pre {
    padding: 1em;
    overflow-x: auto;
  }

  & iframe,
  & video {
    max-width: 100%;
  }
`;

const LoadingContainer = styled.div<{ readOnly?: boolean }>`
  height: ${({ readOnly }) => (readOnly ? "100%" : "300px")};
  border: ${({ readOnly }) => (readOnly ? "none" : "1px solid #CCCCCC")};
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
`;

const LoadingText = styled.div`
  color: #666;
  font-size: 14px;
`;

export const Editor = styled(QuillWrapper)`
  width: 100%;
  min-width: 613px;
  height: 100%;
  border: ${({ readOnly }) => (readOnly ? "none" : "1px solid #CCCCCC")};
  border-radius: 8px;

  ${({ theme }) => theme.breakpoints.between("sm", "xl")} {
    min-width: 100%;
  }

  ${({ theme }) => theme.breakpoints.between("xxs", "sm")} {
    min-width: 100px;
  }

  & .ql-toolbar {
    display: ${({ readOnly }) => (readOnly ? "none" : "block")};
    height: 43px;
    border: none;
    border-bottom: 1px solid #cccccc;

    & .ql-formats {
      margin-right: 5px;
    }
  }

  & .ql-container {
    border: none;
    font-family: "Pretendard";
    font-size: 16px;
    padding: ${({ readOnly }) => (readOnly ? undefined : "10px 11px")};
  }

  & .ql-editor {
    height: ${({ readOnly }) => (readOnly ? "100%" : "calc(100% - 43px)")};
    min-height: ${({ readOnly }) => (readOnly ? "unset" : "100px")};
    overflow: auto;
    padding: 0;
    color: #333;

    /* readOnly일 때 기본 패딩 제거 */
    ${({ readOnly }) =>
      readOnly &&
      `
      padding: 12px 0;
    `}
  }

  & .ql-editor.ql-blank::before {
    font-style: normal;
    color: #999;
    content: "";
  }

  & .ql-editor ul,
  & .ql-editor ul li,
  & .ql-editor ol,
  & .ql-editor ol li {
    padding-left: 1em;
  }

  ${Array.from(Array(9).keys())
    .map((k) => k + 1)
    .map(
      (level) =>
        `& .ql-editor .ql-indent-${level} { padding-left: ${level * 1.5}em }`
    )}

  & .ql-snow .ql-picker.ql-size .ql-picker-item::before {
    content: attr(data-value);
  }

  /* 이미지 스타일 */
  & .ql-editor img {
    max-width: 100%;
    height: auto;
  }

  /* readOnly 상태에서 포커스 제거 */
  ${({ readOnly }) =>
    readOnly &&
    `
    & .ql-editor {
      cursor: default;
    }
    & .ql-editor:focus {
      outline: none;
    }
  `}
`;
